import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { SecurityWarning, SecurityBadge } from '@/components/security/SecurityWarning';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Search, MoreVertical, Eye, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';

interface FlaggedQR {
  id: string;
  name: string;
  type: string;
  content_data: any;
  security_status: string;
  security_threats: any;
  security_checked_at: string;
  user_id: string;
  created_at: string;
  user_email?: string;
}

export function SecurityModerationTable() {
  const [flaggedQRs, setFlaggedQRs] = useState<FlaggedQR[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedQR, setSelectedQR] = useState<FlaggedQR | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchFlaggedQRs();
  }, []);

  const fetchFlaggedQRs = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('qr_codes')
        .select(`
          id,
          name,
          type,
          content_data,
          security_status,
          security_threats,
          security_checked_at,
          user_id,
          created_at
        `)
        .in('security_status', ['flagged', 'blocked'])
        .order('security_checked_at', { ascending: false })
        .limit(100);

      if (error) throw error;

      // Fetch user emails
      const qrsWithEmails = await Promise.all(
        (data || []).map(async (qr) => {
          const { data: profile } = await supabase
            .from('profiles')
            .select('email')
            .eq('id', qr.user_id)
            .single();

          return {
            ...qr,
            user_email: profile?.email || 'Unknown',
          };
        })
      );

      setFlaggedQRs(qrsWithEmails as FlaggedQR[]);
    } catch (error: any) {
      console.error('Error fetching flagged QRs:', error);
      toast({
        title: 'Error',
        description: 'Failed to load flagged QR codes',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const updateQRStatus = async (qrId: string, newStatus: string) => {
    try {
      const { error } = await supabase.rpc('mark_qr_security_status', {
        p_qr_code_id: qrId,
        p_status: newStatus,
        p_threats: [],
      });

      if (error) throw error;

      toast({
        title: 'Success',
        description: `QR code marked as ${newStatus}`,
      });

      fetchFlaggedQRs();
    } catch (error: any) {
      console.error('Error updating QR status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update status',
        variant: 'destructive',
      });
    }
  };

  const filteredQRs = flaggedQRs.filter(
    (qr) =>
      qr.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      qr.user_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      qr.type.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search flagged QRs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-300">
              <AlertTriangle className="h-3 w-3 mr-1" />
              {flaggedQRs.filter((qr) => qr.security_status === 'flagged').length} Flagged
            </Badge>
            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300">
              <XCircle className="h-3 w-3 mr-1" />
              {flaggedQRs.filter((qr) => qr.security_status === 'blocked').length} Blocked
            </Badge>
          </div>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>QR Code</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>User</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Threats</TableHead>
              <TableHead>Detected</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredQRs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  No flagged QR codes found
                </TableCell>
              </TableRow>
            ) : (
              filteredQRs.map((qr) => (
                <TableRow key={qr.id}>
                  <TableCell className="font-medium">{qr.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">
                      {qr.type}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">{qr.user_email}</TableCell>
                  <TableCell>
                    <SecurityBadge status={qr.security_status} />
                  </TableCell>
                  <TableCell>
                    {qr.security_threats && Array.isArray(qr.security_threats) && qr.security_threats.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {qr.security_threats.slice(0, 2).map((threat: any, idx: number) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {threat.type.replace(/_/g, ' ').toLowerCase()}
                          </Badge>
                        ))}
                        {qr.security_threats.length > 2 && (
                          <Badge variant="outline" className="text-xs">
                            +{qr.security_threats.length - 2}
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">None listed</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {qr.security_checked_at
                      ? format(new Date(qr.security_checked_at), 'MMM d, yyyy')
                      : 'N/A'}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setSelectedQR(qr)}>
                          <Eye className="h-4 w-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => updateQRStatus(qr.id, 'safe')}>
                          <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                          Mark as Safe
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => updateQRStatus(qr.id, 'blocked')}>
                          <XCircle className="h-4 w-4 mr-2 text-red-600" />
                          Block QR Code
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!selectedQR} onOpenChange={() => setSelectedQR(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>QR Code Security Details</DialogTitle>
            <DialogDescription>Review detailed security information</DialogDescription>
          </DialogHeader>
          {selectedQR && (
            <div className="space-y-4">
              <div>
                <h4 className="font-medium mb-2">QR Code Information</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Name:</span>
                    <p className="font-medium">{selectedQR.name}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Type:</span>
                    <p className="font-medium">{selectedQR.type}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">User:</span>
                    <p className="font-medium">{selectedQR.user_email}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Created:</span>
                    <p className="font-medium">
                      {format(new Date(selectedQR.created_at), 'PPP')}
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-2">Content</h4>
                <div className="p-3 bg-muted rounded-md text-sm font-mono break-all">
                  {typeof selectedQR.content_data === 'string'
                    ? selectedQR.content_data
                    : JSON.stringify(selectedQR.content_data, null, 2)}
                </div>
              </div>

              <SecurityWarning
                status={selectedQR.security_status as any}
                threats={selectedQR.security_threats}
                checkedAt={selectedQR.security_checked_at}
              />

              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    updateQRStatus(selectedQR.id, 'safe');
                    setSelectedQR(null);
                  }}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Mark as Safe
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => {
                    updateQRStatus(selectedQR.id, 'blocked');
                    setSelectedQR(null);
                  }}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Block
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
